import { faker } from "@faker-js/faker";
import type { CollectibleElastic } from "./collectiblesElasticTypes";
import { mockImageWithId } from "@/services/mockingHelpers";

// Seed faker for consistent results during development (comment out for random data each time)
faker.seed(12345);

// Theme definitions
const THEMES = [
  { name: "Star Wars", weight: 3 },
  { name: "City", weight: 2 },
  { name: "Technic", weight: 2 },
  { name: "Creator Expert", weight: 1 },
  { name: "Ninjago", weight: 1 },
  { name: "Harry Potter", weight: 1 },
  { name: "Marvel", weight: 1 },
  { name: "Architecture", weight: 1 },
] as const;

const YEARS = ["2024", "2023", "2022", "2021", "2020"] as const;

// Generate a random theme based on weights
function getRandomTheme(): string {
  const totalWeight = THEMES.reduce((sum, t) => sum + t.weight, 0);
  let random = faker.number.int({ min: 1, max: totalWeight });

  for (const theme of THEMES) {
    random -= theme.weight;
    if (random <= 0) return theme.name;
  }
  return THEMES[0].name;
}

// Generate set names based on theme
function generateSetName(theme: string): string {
  const prefixes: Record<string, string[]> = {
    "Star Wars": ["Millennium Falcon", "X-Wing", "AT-AT", "TIE Fighter", "Death Star", "Mandalorian Starfighter", "Republic Gunship", "Imperial Shuttle"],
    City: ["Police Station", "Fire Station", "Hospital", "Airport", "Train Station", "Construction Site", "Racing Cars", "Space Center"],
    Technic: ["Porsche 911", "Lamborghini", "Ferrari", "Bugatti", "Excavator", "Crane", "Racing Car", "Monster Truck"],
    "Creator Expert": ["Haunted House", "Roller Coaster", "Ferris Wheel", "Modular Building", "Vintage Car", "Flower Bouquet", "Bonsai Tree", "Ship in Bottle"],
    Ninjago: ["Dragon Temple", "Ninja Dojo", "Thunder Raider", "Ice Dragon", "Fire Mech", "Shadow Fortress", "Golden Dragon", "Samurai X Cave"],
    "Harry Potter": ["Hogwarts Castle", "Hogwarts Express", "Diagon Alley", "Quidditch Match", "Forbidden Forest", "Chamber of Secrets", "Astronomy Tower", "Great Hall"],
    Marvel: ["Iron Man Helmet", "Spider-Man", "Avengers Tower", "Infinity Gauntlet", "Thor Hammer", "Black Panther", "Guardians Ship", "Sanctum Sanctorum"],
    Architecture: ["Eiffel Tower", "Statue of Liberty", "Empire State", "Great Wall", "Taj Mahal", "Colosseum", "Sydney Opera", "Big Ben"],
  };

  const setNames = prefixes[theme] ?? prefixes["City"];
  const baseName = faker.helpers.arrayElement(setNames);
  const variant = faker.helpers.maybe(() => faker.helpers.arrayElement(["Ultimate", "Deluxe", "Limited Edition", "Collector's"]), { probability: 0.2 });

  return variant ? `LEGO ${theme} ${variant} ${baseName}` : `LEGO ${theme} ${baseName}`;
}

// Generate price based on piece count
function generatePrice(pieceCount: number): number {
  // Roughly 0.10-0.15 CZK per piece with some variance
  const pricePerPiece = faker.number.float({ min: 0.08, max: 0.18, fractionDigits: 2 });
  const basePrice = Math.round(pieceCount * pricePerPiece);
  // Round to nice price points
  return Math.round(basePrice / 100) * 100 + faker.helpers.arrayElement([0, 99, -1]);
}

// Create theme object matching the actual schema
function createTheme(name: string) {
  return {
    name,
    release: faker.date.past({ years: 5 }).toISOString(),
    symbol: faker.helpers.maybe(() => faker.image.url(), { probability: 0.3 }) ?? null,
  };
}

// Create parameter object matching the actual schema
function createParam(id: number, name: string, valueId: number, value: string) {
  return { id, name, valueId, value };
}

// Generate a single mock collectible
function generateCollectible(id: number): CollectibleElastic {
  const theme = getRandomTheme();
  const year = faker.helpers.arrayElement(YEARS);
  const pieceCount = faker.number.int({ min: 100, max: 8000 });
  const price = generatePrice(pieceCount);

  return {
    id,
    categoryId: 1,
    name: generateSetName(theme),
    theme: createTheme(theme),
    prices: [{ currencyId: 1, price, type: "retail" }],
    parameters: [createParam(1, "Rok vydání", id * 10 + 1, year), createParam(2, "Počet dílků", id * 10 + 2, String(pieceCount))],
    images: [mockImageWithId(), mockImageWithId(), mockImageWithId()].filter((i) => i !== null),
    ownedCount: faker.number.int({ min: 0, max: 20 }),
  };
}

// Generate mock collectibles
const MOCK_ITEM_COUNT = 50;
const mockCollectibles: CollectibleElastic[] = Array.from({ length: MOCK_ITEM_COUNT }, (_, i) => generateCollectible(i + 1));

/**
 * Regenerate mock data with a new seed (for testing different scenarios)
 */
export function resetMockData(seed?: number): void {
  faker.seed(seed ?? Date.now());
  mockCollectibles.length = 0;
  mockCollectibles.push(...Array.from({ length: MOCK_ITEM_COUNT }, (_, i) => generateCollectible(i + 1)));
}
